import type { IconName, IconPrefix } from '@fortawesome/fontawesome-svg-core'
import type { VariantProps } from 'cva'
import { cva, cx } from 'cva'
import type { ComponentProps, FC } from 'react'
import type { FontAwesomeIconProps } from './FontAwesomeIcon'
import { FontAwesomeIcon } from './FontAwesomeIcon'

const iconCircleVariants = cva({
	base: cx(
		'icon-circle rounded-full flex items-center justify-center flex-shrink-0',
		'group-focus-visible:outline-2 group-focus-visible:outline-offset-1',
	),
	variants: {
		size: {
			sm: 'w-7 h-7', // 28px
			md: 'w-11 h-11 layout-narrow:w-7 layout-narrow:h-7', // 44px
			responsive: 'w-7 h-7 tablet-l:w-11 tablet-l:h-11 layout-narrow:w-7 layout-narrow:h-7', // 28px, tablet-l: 44px
		},
		theme: {
			white: null,
			blue: null,
		},
		intent: {
			primary: null,
			secondary: 'border-(length:--stroke-sm)',
			toggled: cx('bg-blue-shade-70 text-white'),
		},
	},
	compoundVariants: [
		{
			theme: 'white',
			intent: 'primary',
			className: cx(
				'bg-blue text-white',
				'group-hover:bg-blue-shade-40',
				'group-active:bg-blue-shade-70',
			),
		},
		{
			theme: 'white',
			intent: 'secondary',
			className: cx(
				'bg-white border-blue text-blue',
				'group-hover:bg-blue-tint-10 group-hover:border-blue-shade-40 group-hover:text-blue-shade-40',
				'group-active:bg-blue-shade-70 group-active:border-blue-shade-70 group-active:text-white',
			),
		},
		{
			theme: 'blue',
			intent: 'primary',
			className: cx(
				'bg-white text-blue',
				'group-hover:bg-blue-tint-20 group-hover:text-blue-shade-70',
				'group-active:bg-blue-shade-70 group-active:text-white',
			),
		},
		{
			theme: 'blue',
			intent: 'secondary',
			className: cx(
				'bg-blue border-white text-white',
				'group-hover:bg-blue-shade-40',
				'group-active:bg-blue-shade-70 group-active:border-blue-shade-70',
			),
		},
	],
	defaultVariants: {
		size: 'md',
		theme: 'white',
		intent: 'primary',
	},
})
export type IconCircleVariants = VariantProps<typeof iconCircleVariants>

export type IconCircleProps = ComponentProps<'div'> &
	IconCircleVariants & {
		icon: [IconPrefix, IconName]
		className?: string
	}

const fontAwesomeSizeMap: Record<
	NonNullable<IconCircleVariants['size']>,
	FontAwesomeIconProps['size']
> = {
	sm: 'xs',
	md: 'md',
	responsive: 'responsive',
}

export const IconCircle: FC<IconCircleProps> = ({
	icon,
	size = 'md',
	intent,
	theme,
	className,
	...props
}) => {
	return (
		<span className={iconCircleVariants({ size, intent, theme, className })} {...props}>
			<FontAwesomeIcon icon={icon} size={fontAwesomeSizeMap[size]} />
		</span>
	)
}
